import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { buildDocumentPeekRows } from "@/lib/documentPeekRows";
import { fetchApInvoice } from "@/pages/accounting/apInvoicesApi";
import {
  formatReceiptDiscrepancyDate,
  formatReceiptDiscrepancyMoney,
  formatReceiptDiscrepancyQuantity,
} from "@/pages/accounting/receiptDiscrepancyApi";

export function vendorInvoicePeekQueryKey(invoiceId: string): readonly ["accounting", "ap-invoice", string] {
  return ["accounting", "ap-invoice", invoiceId];
}

function lineDetail(quantity: string, unitPrice: string | null, currency: string | null): string {
  const parsedQuantity = Number(quantity);
  const quantityText = Number.isFinite(parsedQuantity)
    ? formatReceiptDiscrepancyQuantity(parsedQuantity)
    : quantity.trim();
  const parsedPrice = unitPrice == null || unitPrice.trim() === "" ? null : Number(unitPrice);
  const money = formatReceiptDiscrepancyMoney(
    parsedPrice != null && Number.isFinite(parsedPrice) ? parsedPrice : null,
    currency,
  );
  if (money === "") return quantityText;
  return `${quantityText} @ ${money}`;
}

export function VendorInvoiceDetailHoverHint({ invoiceId }: { invoiceId: string }): JSX.Element {
  const detailQuery = useQuery({
    queryKey: vendorInvoicePeekQueryKey(invoiceId),
    queryFn: () => fetchApInvoice(invoiceId),
    staleTime: 30_000,
  });

  if (detailQuery.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading vendor invoice…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-xs text-slate-600">Could not load vendor invoice preview.</p>;
  }

  const invoice = detailQuery.data;
  return (
    <HoverHintDefinitionGrid
      rows={buildDocumentPeekRows({
        header: [
          {
            label: "Invoice",
            value: invoice.vendor_invoice_number.trim() !== "" ? invoice.vendor_invoice_number : invoice.id,
          },
          { label: "Status", value: invoice.status_label },
          { label: "Date", value: formatReceiptDiscrepancyDate(invoice.invoice_date) },
          { label: "Supplier", value: invoice.supplier_name },
          { label: "Purchase order", value: invoice.purchase_order_number },
        ],
        lines: invoice.lines.map((line) => ({
          sku: line.sku,
          detail: lineDetail(line.quantity, line.unit_price, invoice.currency),
        })),
      })}
    />
  );
}
